import { get, isNumber, max } from 'radash';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';

import { DiagramComponentInstance, powerConverter, Simulation, VoltageType } from 'models';
import { FormatHelpers } from 'helpers/formatters';
import { round } from 'helpers/round';

import { DateService } from 'services/DateService';

const SimulationHelpers = {
    swr: {
        simulation: (diagramId: string) => `/diagram/${diagramId}/simulations`,
        simulationByType: (id: string, type: string) => `${type}:${id}`,
        resultFileByType: (id: string, type: string) => `${type}:resultFile:${id}`,
    },

    generateName: {
        simulation: () => `Simulation ${DateService.format(new Date())}`,
    },

    componentHasSimulationFields: (componentInstance: DiagramComponentInstance) => {
        const componentDefinition = ComponentInstanceHelpers.definition(componentInstance.componentType);

        const hasControlMethods =
            get(componentInstance?.specifications?.electrical?.ports?.[0], 'DC.controlMethods') !== undefined ||
            get(componentInstance?.specifications?.electrical?.ports?.[0], 'AC.controlMethods') !== undefined;

        const hasPowerField = [
            'solar',
            'wind',
            'hydro',
            'generator',
            'load',
            'charger',
            'light',
            'hvac',
            'motor',
        ].includes(componentInstance.componentType);

        return (
            componentDefinition.ports.powerFlowDirections.length > 1 ||
            hasControlMethods ||
            'energyCapacity' in componentInstance.specifications.electrical ||
            hasPowerField
        );
    },

    formatMultiPhaseLabel: (values: number[], formatSingle: (v: number) => string): string | null => {
        if (values.length === 0) return null;

        if (values.length === 1 || SimulationHelpers.isBalanced(values)) {
            return formatSingle(values[0]) || null;
        }

        const formatted = values.map((v) => formatSingle(v));
        const parts = formatted.map((f) => f.split(/[\s\u00a0]/));
        const lastUnit = parts[parts.length - 1][1];

        if (parts.every((p) => p[1] === lastUnit)) {
            return `${parts.map((p) => p[0]).join(' / ')} ${lastUnit}`;
        }

        return formatted.join(' / ');
    },

    isBalanced: (values: number[], tolerance = 0.01) => {
        if (values.length <= 1) return true;
        const absValues = values.map(Math.abs);
        const avg = absValues.reduce((sum, v) => sum + v, 0) / absValues.length;
        if (avg === 0) return true;
        return absValues.every((v) => Math.abs(v - avg) / avg <= tolerance);
    },

    getPhaseVoltages: (
        source: Simulation['result']['connections']['number']['from'],
        timestamp: number = 0,
    ): number[] => {
        const l1 = source['L1']?.voltage?.[timestamp];
        const l2 = source['L2']?.voltage?.[timestamp];
        const l3 = source['L3']?.voltage?.[timestamp];

        if (isNumber(l1) && isNumber(l2) && isNumber(l3)) {
            return [l1, l2, l3];
        }

        const single = SimulationHelpers.getComponentVoltage(source, timestamp);
        return isNumber(single) ? [single] : [];
    },

    getPhaseCurrents: (
        lines: Simulation['result']['connections']['number']['lines'],
        timestamp: number = 0,
    ): number[] => {
        const l1 = lines['L1']?.current?.[timestamp];
        const l2 = lines['L2']?.current?.[timestamp];
        const l3 = lines['L3']?.current?.[timestamp];

        if (isNumber(l1) && isNumber(l2) && isNumber(l3)) {
            return [l1, l2, l3];
        }

        const single = SimulationHelpers.getConnectionCurrent(lines, timestamp);
        return isNumber(single) ? [single] : [];
    },

    formatConnectionResult: (
        simulationConnection: Simulation['result']['connections']['number'],
        voltageType: VoltageType,
        timestamp: number = 0,
    ) => {
        const { from, to, lines, power } = simulationConnection;

        const toVoltages = SimulationHelpers.getPhaseVoltages(to, timestamp);
        const fromVoltages = SimulationHelpers.getPhaseVoltages(from, timestamp);
        const connectionCurrents = SimulationHelpers.getPhaseCurrents(lines, timestamp);

        const phasePower = isNumber(power?.[timestamp])
            ? power![timestamp]
            : SimulationHelpers.getTotalPhasePower(from, timestamp);

        const connectionPower = isNumber(phasePower)
            ? phasePower
            : SimulationHelpers.getConnectionPower({
                  current: connectionCurrents[0],
                  toVoltage: toVoltages[0],
                  fromVoltage: fromVoltages[0],
                  voltageType,
              });

        const roundedToVoltages = toVoltages.map((v) => Math.abs(round(v, 1)));
        const roundedFromVoltages = fromVoltages.map((v) => Math.abs(round(v, 1)));
        const roundedConnectionCurrents = connectionCurrents.map((i) => Math.abs(round(i, 3)));

        return {
            // Per-phase arrays (L1/L2/L3 for three-phase, or single-element for DC/single-phase)
            toVoltages: roundedToVoltages,
            fromVoltages: roundedFromVoltages,
            connectionCurrents: roundedConnectionCurrents,
            connectionPower: isNumber(connectionPower) ? Math.abs(round(connectionPower, 1)) : null,
            // L1 / DC values kept for limit/percentage calculations
            toVoltage: roundedToVoltages[0] ?? null,
            fromVoltage: roundedFromVoltages[0] ?? null,
            connectionCurrent: roundedConnectionCurrents[0] ?? null,
        };
    },

    getConnectionCurrent: (lines: Simulation['result']['connections']['number']['lines'], timestamp: number = 0) => {
        // AC
        for (const phase of ['L1', 'L2', 'L3'] as const) {
            if (isNumber(lines[phase]?.current?.[timestamp])) {
                return lines[phase].current[timestamp];
            }
        }

        // DC
        if (isNumber(lines['L+']?.current?.[timestamp])) {
            return lines['L+'].current[timestamp];
        }

        if (isNumber(lines['M']?.current?.[timestamp])) {
            return lines['M'].current[timestamp];
        }

        // Grounding connections carry only the PE return current
        return lines['PE']?.current?.[timestamp];
    },

    getConnectionPowerLosses: (
        simulationConnection: Simulation['result']['connections']['number'],
        timestamp: number = 0,
    ): string | null => {
        const watts = simulationConnection?.powerLosses?.[timestamp];

        if (!isNumber(watts)) {
            return null;
        }

        return `Δ ${FormatHelpers.formatMeasurement({ value: Math.abs(watts), unit: 'W' }, powerConverter)}`;
    },

    getConnectionPowerLossesInPercent: (
        simulationConnection: Simulation['result']['connections']['number'],
        connectionPower: number | null,
        timestamp: number = 0,
    ): string | null => {
        const watts = simulationConnection?.powerLosses?.[timestamp];

        if (!isNumber(watts)) {
            return null;
        }

        const percent = connectionPower ? (Math.abs(watts) / connectionPower) * 100 : 0;

        return `Δ ${percent.toFixed(1)}%`;
    },

    getStateOfCharge: (simulationResult: Simulation['result']['componentInstances'][number], timestamp: number = 0) => {
        const soc = simulationResult?.stateOfCharge?.[timestamp];

        if (!isNumber(soc)) {
            return null;
        }

        // Returns a relative number between 0.0 and 1.0 which we will convert to a percentage
        return `${(soc * 100).toFixed(0)}%`;
    },

    getStateOfChargeColor: (
        simulationResult: Simulation['result']['componentInstances'][number],
        timestamp: number = 0,
    ) => {
        const soc = simulationResult?.stateOfCharge?.[timestamp];

        if (!isNumber(soc)) {
            return 'var(--mantine-color-gray-5)';
        }

        if (soc < 0.2) {
            return 'var(--mantine-color-red-7)';
        }

        if (soc < 0.5) {
            return 'var(--mantine-color-orange-7)';
        }

        if (soc < 0.8) {
            return 'var(--mantine-color-yellow-7)';
        }

        return 'var(--mantine-color-green-7)';
    },

    getPowerLosses: (
        simulationResult: Simulation['result']['componentInstances'][number],
        timestamp: number,
        showInPercent?: boolean,
    ) => {
        if (!simulationResult?.powerLosses || !isNumber(simulationResult.powerLosses[timestamp])) {
            return null;
        }

        if (showInPercent) {
            const percent = SimulationHelpers.computePowerLossesInPercent(simulationResult, timestamp);
            return percent === null ? null : `Δ ${percent}`;
        }

        return `Δ ${FormatHelpers.formatMeasurement(
            { value: simulationResult.powerLosses[timestamp], unit: 'W' },
            powerConverter,
        )}`;
    },

    computePowerLossesInPercent: (
        simulationResult: Simulation['result']['componentInstances'][number],
        timestamp: number,
    ) => {
        const powerLosses = simulationResult?.powerLosses?.[timestamp];

        if (!isNumber(powerLosses)) {
            return null;
        }

        let totalInPower = 0;

        for (const port of Object.values(simulationResult.ports)) {
            const power = port.power?.[timestamp] ?? 0;

            if (power > 0) {
                totalInPower += power;
            }
        }

        const percent = totalInPower > 0 ? (powerLosses / totalInPower) * 100 : 0;

        return `${percent.toFixed(0)}%`;
    },

    getTotalPhasePower: (
        source: Simulation['result']['connections']['number']['from'],
        timestamp: number = 0,
    ): number | null => {
        const phases = ['L1', 'L2', 'L3', 'L+', 'M', 'L-'] as const;
        let total = 0;
        let found = false;

        for (const phase of phases) {
            const power = source[phase]?.power?.[timestamp];
            if (isNumber(power)) {
                total += power;
                found = true;
            }
        }

        return found ? total : null;
    },

    getConnectionPower: ({
        current,
        toVoltage,
        fromVoltage,
        voltageType,
    }: {
        current: number | undefined;
        toVoltage: number | undefined;
        fromVoltage: number | undefined;
        voltageType: VoltageType;
    }) => {
        if (typeof current === 'undefined') {
            return null;
        }

        const maxVoltage = max([toVoltage, fromVoltage].filter(isNumber));

        if (maxVoltage === null) {
            return null;
        }

        if (voltageType === 'AC') {
            return current * maxVoltage * Math.sqrt(3);
        }

        return current * maxVoltage;
    },

    getComponentVoltage: (source: Simulation['result']['connections']['number']['from'], timestamp: number = 0) => {
        // AC (single-phase connections may only have L2 or L3, not L1)
        for (const phase of ['L1', 'L2', 'L3'] as const) {
            if (isNumber(source[phase]?.voltage?.[timestamp])) {
                return source[phase].voltage[timestamp];
            }
        }

        // DC
        if (isNumber(source['L+']?.voltage?.[timestamp]) && isNumber(source['M']?.voltage?.[timestamp])) {
            return source['L+'].voltage[timestamp] - source['M'].voltage[timestamp];
        } else if (isNumber(source['L+']?.voltage?.[timestamp]) && isNumber(source['L-']?.voltage?.[timestamp])) {
            return source['L+'].voltage[timestamp] - source['L-'].voltage[timestamp];
        } else if (isNumber(source['L-']?.voltage?.[timestamp]) && isNumber(source['M']?.voltage?.[timestamp])) {
            return source['M'].voltage[timestamp] - source['L-'].voltage[timestamp];
        }

        // Grounding connections have no active-conductor pair; the PE node voltage is the measurement
        // itself (0 at the power-flow reference, lifted above 0 during a ground fault)
        if (isNumber(source['PE']?.voltage?.[timestamp])) {
            return source['PE'].voltage[timestamp];
        }
    },

    getScenarioRange: <T>(
        scenario: { min?: T; max?: T } | undefined,
        read: (scenarioValue: T) => number | null | undefined,
    ): { min: number; max: number } | null => {
        const minValue = scenario?.min ? read(scenario.min) : undefined;
        const maxValue = scenario?.max ? read(scenario.max) : undefined;

        if (!isNumber(minValue) || !isNumber(maxValue)) return null;

        return {
            min: Math.min(Math.abs(minValue), Math.abs(maxValue)),
            max: Math.max(Math.abs(minValue), Math.abs(maxValue)),
        };
    },

    withScenarioRange: (
        label: string | null,
        range: { min: number; max: number } | null,
        format: (value: number) => string,
    ): string | null => {
        if (!label || !range) return label;

        return `${label} (${format(range.min)}–${format(range.max)})`;
    },
};

export { SimulationHelpers };
